/**
 * The knowledge base `wayai knowledge refresh-preview` talks to, through the `/api/data` proxy over
 * the CLI's one login: the base, its previews, and the preview file snapshot it pages.
 *
 * Every request is paced at four fifths of the person's per-minute data budget — the budget the
 * person's other commands and web console share — and waits out a 429 when that budget is spent.
 */

import { ApiError } from '../lib/api-client.js';
import { Pacer, type PacerOptions } from '../communities/pacer.js';
import type { DataClient } from '../data/client.js';
import { getBaseRecord } from '../data/config-as-code/api.js';
import { pathSegment } from '../data/helpers.js';
import { filesSyncRequestsPerMinute } from '../data/files-sync/sync.js';
import type { BaseRow, PreviewKnowledgeBase } from './refresh-preview.js';

/** The data budget's pacer, at the rate `wayai files push` keeps, saying why it waits. */
function dataPacer(): Pacer {
  const perMinute = filesSyncRequestsPerMinute();
  const onWait: PacerOptions['onWait'] = (ms, reason) =>
    console.error(
      reason === 'paced'
        ? `  Pacing data requests at ${perMinute} a minute: waiting ${Math.ceil(ms / 1000)} s.`
        : `  The data request budget is spent (your other commands and the web console share it): waiting ${Math.ceil(ms / 1000)} s.`,
    );
  return new Pacer({ perMinute, onWait });
}

/** `read`'s answer, or null when the base answers 404. */
async function unlessMissing<T>(read: () => Promise<T>): Promise<T | null> {
  try {
    return await read();
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

/** The knowledge base, through the data proxy. */
export function knowledgeBase(client: DataClient): PreviewKnowledgeBase {
  const pacer = dataPacer();
  const baseSeg = (id: string) => pathSegment(id, '--base');

  return {
    getBase: (id) => unlessMissing<BaseRow>(() => pacer.run(() => getBaseRecord(client, id))),
    listPreviews: (id) =>
      pacer.run(() =>
        client.collectPages<BaseRow>((cursor) => `/v1/${baseSeg(id)}/previews${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`),
      ),
    // `requestRaw`: the page is read whole, `{ data: … }`, as WayAI's copy of its answer declares it
    // (`@wayai/core/contracts` `rekorFileSnapshotAnswerSchema`).
    snapshotPage: (preview, cursor) =>
      pacer.run(() => client.requestRaw('POST', `/v1/${baseSeg(preview)}/files/snapshot`, cursor === undefined ? {} : { cursor })),
  };
}
